// ─────────────────────────────────────────────
//  WelcomePage — the four-step onboarding wizard, and the board preview that
//  follows it.
//
//  THE PAGE HOLDS NO STEP STATE. It renders whatever nextStepKey() reports for
//  the answers already on the profile, and each completed step is persisted
//  immediately with updateUser(). That is what makes the wizard resumable:
//  close the app after step two and it reopens on step three, because the
//  answers ARE the progress. A separate `currentStep` field would be a second
//  source of truth, and the two would drift the first time a write failed.
//
//  The draft value for the step in progress lives in <WizardStep>, which is
//  keyed by the step key — so when the profile advances, React unmounts the old
//  step and mounts a fresh, empty one. No manual reset to forget.
//
//  Numeric answers parse with Number.parseInt and hand NaN straight to
//  validateStep, which rejects it. Never Number(raw): Number("") and
//  Number(null) are both 0, which is a valid-looking answer this codebase has
//  been bitten by before.
//
//  Components are imported by path rather than through ../components, whose
//  barrel drags in Firebase-dependent components (messaging, board) that this
//  page does not use.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "../components/Button";
import Card from "../components/Card";
import Field from "../components/Field";
import { useAuth } from "../context/AuthContext";
import {
  FACILITY_TYPES,
  WIZARD_STEPS,
  answerFieldFor,
  nextStepKey,
  validateStep,
} from "../domain/onboarding";
import { updateUser } from "../firebase/firestore";
import { seedSandbox } from "../sandbox/seed";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;

// How many room names the preview spells out before summarising the rest.
const PREVIEW_ROOMS_SHOWN = 8;

const NUMBER_STEPS = ["rooms", "team"];

const FIELD_STEPS = {
  you:   { id: "wizard-you",   label: "Your name",          type: "text",   placeholder: "Alex Rivera" },
  org:   { id: "wizard-org",   label: "Organization name",  type: "text",   placeholder: "Mercy General" },
  rooms: { id: "wizard-rooms", label: "Operating rooms",    type: "number", placeholder: "6" },
  team:  { id: "wizard-team",  label: "People on the team", type: "number", placeholder: "12" },
};

export default function WelcomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const answers = (user && user.onboarding) || {};
  const stepKey = nextStepKey(answers);
  const stepIndex = WIZARD_STEPS.findIndex((s) => s.key === stepKey);

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <Card variant="elevated">
          {stepKey ? (
            <>
              {/* The heading and the live region live OUTSIDE the keyed step so
                  they stay mounted as the wizard advances. A role="status"
                  region that is inserted together with its text is not reliably
                  announced; one whose text changes in place is. */}
              <h1 style={styles.h1}>Set up your board</h1>
              <div style={styles.progress} role="status">
                Step {stepIndex + 1} of {WIZARD_STEPS.length}
              </div>
              {/* Keyed by step: a new step is a new component instance, so the
                  draft value and any error start clean without a reset effect. */}
              <WizardStep key={stepKey} stepKey={stepKey} uid={user && user.uid} />
            </>
          ) : (
            <BoardPreview
              answers={answers}
              onOpen={async () => {
                await seedSandbox({ uid: user && user.uid, onboarding: answers });
                // Marking the profile onboarded is what RELEASES the gate.
                // Without it, OnboardingGate sees sandbox && !onboarded and
                // sends /board straight back here — the user is trapped in the
                // wizard with a board that exists and no way to reach it. It
                // must land AFTER the seed, so a failed seed leaves the user on
                // a retryable preview rather than at an empty board.
                await updateUser(user.uid, { onboarded: true });
              }}
              onOpened={() => navigate("/board")}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

// ── One step ─────────────────────────────────
function WizardStep({ stepKey, uid }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const step = WIZARD_STEPS.find((s) => s.key === stepKey);
  const isNumber = NUMBER_STEPS.includes(stepKey);

  const submit = async (event) => {
    event.preventDefault();
    // parseInt, never Number(): Number("") is 0, which would sail past
    // validateStep as a real answer. NaN is rejected by design.
    const answer = isNumber ? Number.parseInt(value, 10) : value.trim();
    const message = validateStep(stepKey, answer);
    if (message) {
      setError(message);
      return;
    }
    setError("");
    setSaving(true);
    try {
      await updateUser(uid, {
        [`onboarding.${answerFieldFor(stepKey)}`]: answer,
        // ⚠️ THE NAME STEP ALSO WRITES THE PROFILE NAME. Left only inside
        // `onboarding`, the wizard would collect a name the board never reads:
        // every surface prints `user.name`, which for a self-signed-up account
        // is the email local-part. Written together so the two cannot drift.
        ...(stepKey === "you" ? { name: String(answer).trim() } : {}),
      });
      // No step advance here. The profile snapshot advances it, which is the
      // same path a reload takes — one behaviour to get right, not two.
    } catch {
      setError("Could not save that answer. Check your connection and try again.");
      setSaving(false);
    }
  };

  const field = FIELD_STEPS[stepKey];

  return (
    // noValidate: the inputs stay marked `required` for assistive technology,
    // but the BROWSER must not intercept the submit. Native constraint
    // validation silently refuses to fire onSubmit on an empty field and shows
    // its own transient bubble, so validateStep would never run and the
    // role="alert" region would stay empty — the one message a screen reader
    // is listening for.
    <form onSubmit={submit} noValidate>
      {field ? (
        <>
          <h2 style={styles.h2}>{step.title}</h2>
          <Field
            id={field.id}
            label={field.label}
            type={field.type}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={error || undefined}
            required
          />
        </>
      ) : (
        <FacilityTypeChoice
          value={value}
          onChange={setValue}
          title={step.title}
          error={error}
        />
      )}

      <Button variant="primary" size="full" type="submit" loading={saving}>
        Continue
      </Button>
    </form>
  );
}

// ── The facility-type radios ─────────────────
// A <fieldset> whose <legend> carries the step title, so a screen reader reads
// the question before every option. This step therefore has no <h2>: the legend
// is the question, and duplicating it as a heading would announce it twice.
function FacilityTypeChoice({ value, onChange, title, error }) {
  return (
    <fieldset style={styles.fieldset}>
      <legend style={styles.legend}>{title}</legend>
      {FACILITY_TYPES.map((type) => (
        <label key={type} style={styles.radioRow}>
          <input
            type="radio"
            name="wizard-facility-type"
            value={type}
            checked={value === type}
            onChange={() => onChange(type)}
            style={styles.radio}
          />
          <span style={styles.radioLabel}>{type}</span>
        </label>
      ))}
      {error ? (
        <div role="alert" style={styles.error}>{error}</div>
      ) : null}
    </fieldset>
  );
}

// ── The preview ──────────────────────────────
function BoardPreview({ answers, onOpen, onOpened }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Defensive: the preview only renders once nextStepKey says every answer is
  // present, but a hand-edited or half-imported profile must not turn into
  // Array.from({ length: NaN }) further down.
  const orCount = Number.isFinite(answers.orCount) ? answers.orCount : 0;
  const shown = Math.min(orCount, PREVIEW_ROOMS_SHOWN);
  const rooms = Array.from({ length: shown }, (_, i) => `OR ${i + 1}`);
  const remaining = orCount - shown;

  const open = async () => {
    setError("");
    setBusy(true);
    try {
      await onOpen();
      onOpened();
    } catch {
      // Seeding failed, so the board would be empty. Say so and stay put
      // rather than navigating to a board with nothing on it.
      setError("Could not build your board. Try again.");
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={styles.eyebrow}>Your board is ready to build</div>
      <h1 style={styles.h1}>{answers.orgName}</h1>
      <div style={styles.summary}>
        {orCount} operating rooms · {answers.teamSize} people
      </div>

      <h2 style={styles.h2}>Rooms we will create</h2>
      <ul style={styles.roomList}>
        {rooms.map((name) => (
          <li key={name} style={styles.room}>{name}</li>
        ))}
      </ul>
      {remaining > 0 ? (
        <div style={styles.note}>…and {remaining} more.</div>
      ) : null}

      <p style={styles.note}>
        We have added a few sample providers and a sample day so the board has
        something to show. You can delete them any time.
      </p>

      {error ? <div role="alert" style={styles.error}>{error}</div> : null}

      <Button variant="primary" size="full" onClick={open} loading={busy}>
        Open my board
      </Button>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: color.surface.base,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space.md,
    fontFamily: font.family,
  },
  // Never wider than the viewport: the wizard has to work at 375 px with no
  // horizontal scroll.
  shell: { width: 460, maxWidth: "100%" },
  h1: {
    fontSize: font.size.display,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight.tight,
    color: color.accent.primary,
    fontFamily: font.family,
    margin: 0,
    // A one-word organization name must wrap rather than push the card wider
    // than a 375 px viewport.
    overflowWrap: "anywhere",
  },
  h2: {
    fontSize: font.size.heading,
    fontWeight: font.weight.semibold,
    lineHeight: font.lineHeight.tight,
    color: color.text.primary,
    fontFamily: font.family,
    margin: `${space.md}px 0 ${space.sm}px`,
  },
  progress: {
    fontSize: font.size.label,
    fontWeight: font.weight.medium,
    color: color.text.muted,
    fontFamily: font.family,
    marginTop: space.xs,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  eyebrow: {
    fontSize: font.size.label,
    fontWeight: font.weight.medium,
    color: color.text.muted,
    fontFamily: font.family,
    marginBottom: space.xs,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  summary: {
    fontSize: font.size.body,
    color: color.text.secondary,
    fontFamily: font.family,
    marginTop: space.sm,
  },
  fieldset: {
    border: "none",
    padding: 0,
    margin: `0 0 ${space.md}px`,
    minWidth: 0,
  },
  legend: {
    fontSize: font.size.heading,
    fontWeight: font.weight.semibold,
    lineHeight: font.lineHeight.tight,
    color: color.text.primary,
    fontFamily: font.family,
    padding: 0,
    margin: `${space.md}px 0 ${space.sm}px`,
  },
  // 44 px minimum touch target (WCAG 2.2 AA), the whole row being the target.
  radioRow: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    minHeight: 44,
    padding: `0 ${space.sm}px`,
    marginBottom: space.xs,
    border: "1px solid " + color.border.default,
    borderRadius: radius.md,
    background: color.surface.card,
    cursor: "pointer",
  },
  radio: { width: 20, height: 20, accentColor: color.accent.primary, flexShrink: 0 },
  radioLabel: {
    fontSize: font.size.body,
    color: color.text.primary,
    fontFamily: font.family,
  },
  roomList: {
    display: "flex",
    flexWrap: "wrap",
    gap: space.xs,
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  room: {
    background: color.surface.raised,
    border: "1px solid " + color.border.default,
    borderRadius: radius.sm,
    padding: `${space.xs}px ${space.sm}px`,
    fontSize: font.size.label,
    color: color.text.secondary,
    fontFamily: font.family,
  },
  note: {
    fontSize: font.size.label,
    lineHeight: font.lineHeight.normal,
    color: color.text.muted,
    fontFamily: font.family,
    margin: `${space.sm}px 0 ${space.md}px`,
  },
  // Error text on the white card surface: #dc2626 on #ffffff is 4.85:1.
  error: {
    background: color.surface.card,
    border: "1px solid " + color.accent.error,
    color: color.accent.error,
    borderRadius: radius.sm,
    padding: "8px 12px",
    fontSize: font.size.label,
    marginBottom: space.sm,
    fontFamily: font.family,
  },
};
